import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpParams,
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Labels } from '@core/constants/labels';
import { ExamNonReadCounts } from '@core/models/exam-non-read-counts.model';
import { ExamRequest } from '@core/models/exam-request.model';
import { FilterDataExamRequests } from '@core/models/filter-data-exam-requests.model';
import { FilterDataMonitoredCases } from '@core/models/filter-data-monitored-cases.models';
import { Message } from '@core/models/message.model';
import { MonitoredCase } from '@core/models/monitored-case.model';
import { Patient } from '@core/models/patient.model';
import { Report } from '@core/models/report.model';
import { environment } from '@environment/environment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, catchError, map, throwError } from 'rxjs';

@Injectable()
export class ExamService {
  currentExam = new BehaviorSubject<ExamRequest>(new ExamRequest());
  currentNonReads = new BehaviorSubject<ExamNonReadCounts[]>([]);
  filterData: FilterDataExamRequests = new FilterDataExamRequests();
  filterUpdateEmitter: EventEmitter<FilterDataExamRequests> =
    new EventEmitter<FilterDataExamRequests>();

  infoToAdd: any[] = [];

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private toastr: ToastrService,
  ) {}

  updateFilters(filter: FilterDataExamRequests) {
    this.filterData = filter;
    this.filterUpdateEmitter.emit(this.filterData);
  }

  setCurrentExam(exam: ExamRequest) {
    this.currentExam.next(exam);
  }

  deleteExam(exam_id: number): Observable<any> {
    const route = `${environment.examInfoBackend}/exam_requests/${exam_id}`;
    return this.httpClient.delete(route);
  }

  getExams(): any {
    const route = `${environment.examInfoBackend}/exam_requests`;
    const params = new HttpParams({ fromObject: this.filterData.toObject() });
    return this.httpClient.get<any>(route, { params, responseType: 'json' });
  }

  getOpenedDiscussions(): Observable<any> {
    const route = `${environment.examInfoBackend}/opened_discussions`;
    return this.httpClient.get<any>(route, {
      responseType: 'json',
    });
  }

  getExamsWithDiscussion(): Observable<ExamNonReadCounts[]> {
    const route = `${environment.examInfoBackend}/exam_requests_with_discussion`;
    return this.httpClient.get<ExamNonReadCounts[]>(route);
  }

  private handle404Error(error: HttpErrorResponse): Observable<never> {
    if (
      error.status === 404 &&
      error.error.error === 'Exam request not found'
    ) {
      this.router.navigate(['/']);
      this.toastr.error(Labels.DASHBOARD.EXAM_NOT_ALLOWED, 'Erro');
    }
    return throwError(() => error);
  }

  getExam(id: number): Observable<ExamRequest> {
    const route = `${environment.examInfoBackend}/exam_requests/${id}`;
    return this.httpClient
      .get<ExamRequest>(route)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  nextExam(id: number): Observable<any> {
    const route = `${environment.examInfoBackend}/exam_requests/${id}/next`;
    return this.httpClient
      .get(route)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  prevExam(id: number): Observable<any> {
    const route = `${environment.examInfoBackend}/exam_requests/${id}/prev`;
    return this.httpClient
      .get(route)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  addMessage(exam_id: number, message: Message): Observable<Message> {
    const route = `${environment.examInfoBackend}/message/at/${exam_id}`;
    return this.httpClient.post<Message>(route, message);
  }

  getPatients(): Observable<Patient[]> {
    const route = `${environment.examInfoBackend}/patient`;
    return this.httpClient
      .get<Patient[]>(route)
      .pipe(
        map((patients) =>
          patients.map((patientData) =>
            Object.assign(new Patient(), patientData),
          ),
        ),
      );
  }

  createPatient(data: any): Observable<Patient> {
    const route = `${environment.examInfoBackend}/patient`;
    return this.httpClient.post<Patient>(route, data);
  }

  saveExam(
    formValue: any,
    imageIds: number[],
    prontuary_file: any,
  ): Observable<ExamRequest> {
    const imageIdsStr = imageIds.join('|');
    const formData: FormData = new FormData();
    if (formValue?.id > 0) {
      formData.append('id', formValue?.id);
    }
    formData.append('patient_id', formValue?.patient?.id);
    formData.append('description', formValue?.description);
    formData.append('tissue_related', formValue?.tissue_related);
    formData.append(
      'urgent',
      (formValue?.urgency === Labels.REGISTER_EXAM.URGENCY_YES).toString(),
    );
    formData.append('image_ids', imageIdsStr);
    if (formValue?.assigned_to?.id) {
      formData.append('assigned_to_id', formValue?.assigned_to?.id);
    }
    formData.append('file', prontuary_file);
    const route = `${environment.examInfoBackend}/exam_requests`;
    return this.httpClient
      .post<ExamRequest>(route, formData)
      .pipe(catchError((error) => throwError(error)));
  }

  saveProntuary(data: FormData): Observable<HttpEvent<any>> {
    const route = `${environment.examInfoBackend}/prontuary`;
    return this.httpClient.post<HttpEvent<any>>(route, data, {
      reportProgress: true,
      observe: 'events',
    });
  }

  getReportTemplates(): Observable<Report[]> {
    const route = `${environment.examInfoBackend}/reports/templates`;
    return this.httpClient.get<Report[]>(route);
  }

  saveReport(exam_id: number, html_data: string): Observable<Report> {
    const route = `${environment.examInfoBackend}/reports/${exam_id}`;
    const payload = {
      examId: exam_id,
      name: '',
      html_content: html_data,
      is_template: false,
    };
    return this.httpClient.post<Report>(route, payload);
  }

  releaseExam(exam_id: number, html_data: string): Observable<ExamRequest> {
    const route = `${environment.examInfoBackend}/exam_requests/release/${exam_id}`;
    const payload = {
      examId: exam_id,
      name: '',
      html_content: html_data,
      is_template: false,
    };
    return this.httpClient
      .post<ExamRequest>(route, payload)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  reopenExam(exam_id: number, html_data: string): Observable<ExamRequest> {
    const route = `${environment.examInfoBackend}/exam_requests/reopen/${exam_id}`;
    const payload = {
      examId: exam_id,
      name: '',
      html_content: html_data,
      is_template: false,
    };
    return this.httpClient
      .post<ExamRequest>(route, payload)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  fetchIdByCode(exam_code: string): Observable<{ id: number }> {
    const route = `${environment.examInfoBackend}/lis/get_exam_id/${exam_code}`;
    return this.httpClient
      .get<{ id: number }>(route)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  fetchExamAndImageInfoByBarcode(
    image_barcode: string,
  ): Observable<{ exam_id: number; image_id: number }> {
    const route = `${environment.examInfoBackend}/lis/get_exam_data_from_image_id/${image_barcode}`;
    return this.httpClient
      .get<{ exam_id: number; image_id: number }>(route)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  startReportGeneration(exam_id: number): Observable<any> {
    const route = `${environment.examInfoBackend}/exam_requests/${exam_id}/generate_pdf`;
    return this.httpClient
      .post<any>(route, {})
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  getReport(exam_id: number): Observable<Report> {
    const route = `${environment.examInfoBackend}/reports/${exam_id}`;
    return this.httpClient
      .get<Report>(route)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  getReportPdf(report_id: number): Observable<Blob> {
    const targetUrl = `${environment.examInfoBackend}/reports/download/${report_id}`;
    return this.httpClient
      .get(targetUrl, { responseType: 'blob' })
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  createExamAccessUrl(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/create_exam_access_url`;
    return this.httpClient
      .post(url, data)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handle404Error(error)),
      );
  }

  revokeExamAccessUrl(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/revoke_exam_access_url`;
    return this.httpClient
      .post(url, data)
      .pipe(catchError((error) => throwError(() => error)));
  }

  changeAllowBarcodeExamAccessUrl(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/change_allow_barcode_exam_access_url`;
    return this.httpClient
      .post(url, data)
      .pipe(catchError((error) => throwError(() => error)));
  }

  getMyExternalUserAuthorization(exam_id: number): Observable<any> {
    const url = `${environment.examInfoBackend}/exam_requests/${exam_id}/get_my_external_user_authorization`;
    return this.httpClient
      .get(url)
      .pipe(catchError((error) => throwError(() => error)));
  }

  getAnnotationImageForReport(annotation_id: number) {
    const url = `${environment.examInfoBackend}/annotation/report_image/${annotation_id}`;
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  enqueueInfoToAdd(data: any) {
    this.infoToAdd.push(data);
  }

  hasInfoToAdd() {
    return this.infoToAdd.length > 0;
  }

  getInfoToAdd() {
    return this.infoToAdd.pop();
  }

  getMonitoredExams(filterData: FilterDataMonitoredCases): any {
    const route = `${environment.examInfoBackend}/monitored_cases`;
    const params = new HttpParams({ fromObject: filterData.toObject() });
    return this.httpClient.get<any>(route, { params, responseType: 'json' });
  }

  saveMonitoredExam(code: string): Observable<MonitoredCase> {
    const route = `${environment.examInfoBackend}/monitored_cases`;
    const mc = new MonitoredCase();
    mc.code = code;
    return this.httpClient.post<MonitoredCase>(route, mc);
  }

  deleteMonitoredExam(id: number): Observable<any> {
    const route = `${environment.examInfoBackend}/monitored_cases/${id}`;
    return this.httpClient
      .delete(route)
      .pipe(catchError((error) => throwError(error)));
  }
}
